import { useCallback, useEffect, useState } from "react";

import EditNameModal from "./EditName/EditName";
import EditPasswordModal from "./EditPassword/EditPassword";
import EditBalance from "./EditBalance/EditBalance";
import EditCashDeposit from "./EditCashDeposit/EditCashDeposit";
import AutoSqP from "./AutoSqP/AutoSqP";
import EditCommission from "./EditCommission/EditCommission";
import EditBroker from "./EditBroker/EditBroker";
import EditBrokerSharing from "./EditBrokerSharing/EditBrokerSharing";

import useSize from "../../../Utils/useSize/useSize";
import StatusButton from "./StatusButton/StatusButton";
import ResetPassword from "./StatusButton/ResetPassword";
import ActionButton from "./ActionButton/ActionButton";
import toast from "react-hot-toast";
import Backend from "../../../Backend/Backend";
import Loading from "../../Loading/Loading";

const backend = await new Backend().init();

export default function Edit({ user_id }) {
  const userType = Number(localStorage.getItem("user_type") || 7);
  const windowSize = useSize();

  const [user, setUser] = useState(null);
  const [master, setMaster] = useState({});
  const [broker, setBroker] = useState({});
  const [brokerlist, setBrokerList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [isCashBalanceModalOpen, setIsCashBalanceModalOpen] = useState(false);
  const [isAutoSqPModalOpen, setIsAutoSqPModalOpen] = useState(false);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [isBrokerModalOpen, setIsBrokerModalOpen] = useState(false);
  const [isBrokeSharingModalOpen, setIsBrokeSharingModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const closeAllModals = () => {
    setIsNameModalOpen(false);
    setIsPasswordModalOpen(false);
    setIsBalanceModalOpen(false);
    setIsCashBalanceModalOpen(false);
    setIsAutoSqPModalOpen(false);
    setIsCommissionModalOpen(false);
    setIsBrokerModalOpen(false);
    setIsBrokeSharingModalOpen(false);
  };

  /** --------------------------
   * LOAD USER DATA
   ---------------------------*/
  const loadUser = useCallback(async () => {
    setLoading(true);

    try {
      const payload = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        user_id,
      };

      const r = await backend.load_user_select(payload);
      if (r?.error === "False") {
        setUser(r.user || {});
        setMaster(r.userdata || {});
        setBroker(r.brokerdata || {});
        setBrokerList(r.brokerlist || []);
        setLoading(false);
      } else {
        setLoading(false);
        toast.error(r?.message || "Failed to load user");
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
      toast.error("Error loading user");
    }
  }, [user_id]);

  useEffect(() => {
    if (user_id) loadUser();
  }, [user_id, loadUser]);

  // ESC to close modals
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") closeAllModals();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  /** --------------------------
   * UI HELPERS
   ---------------------------*/
  const formatNum = (v) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(v || 0));

  const containerHeight = windowSize[1] - 170;

  /** --------------------------
   * WHEN USER ID IS MISSING
   ---------------------------*/
  if (!user_id) {
    return (
      <div className="p-6 m-4 bg-red-50 border border-red-200 rounded-xl text-center shadow">
        <h2 className="text-red-600 font-bold text-lg">⚠️ Error</h2>
        <p>User ID is required.</p>
      </div>
    );
  }

  /** --------------------------
   * LOADING STATE
   ---------------------------*/
  if (loading || !user) {
    return (
      <div className="loadingContainer">
        <Loading height={containerHeight} />
      </div>
    );
  }

  /** --------------------------
   * MAIN UI
   ---------------------------*/

  const {
    id,
    broker_id,
    sub_broker_id,
    name,
    username,
    deposit_total,
    deposit_current,
    deposit_interest,
    deposit_interest_rate,
    limit_blocked,
    auto_sq_percentage,
    nse_cash,
    commission_show,
    ledger_view,
    trade,
    fresh_limit,
    options_sell,
    only_sq,
    auto_sq,
    block_symbol_allow,
    watch,
    status,
    alert,
    rupee_dollar,
  } = user;
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 pl-5 pr-5 pb-5"
      style={{ height: containerHeight, width: windowSize[0] - 100 }}
    >
      {/* ------------------------------------
          COLUMN 1 — USER DETAILS
      --------------------------------------*/}
      <div
        style={{
          overflowY: "scroll",
          height: containerHeight - 20,
          paddingBottom: 200,
        }}
        className="bg-[var(--background)] rounded-xl p-5 pb-0 shadow-inner border-2 border-[var(--hover)]"
      >
        <div className="space-y-2 text-sm">
          <DetailRow label="USERNAME" value={username} />

          <DetailRow
            label="NAME"
            value={name}
            buttonLabel={userType !== 5 ? "EDIT" : null}
            onButtonClick={() => setIsNameModalOpen(true)}
          />

          <DetailRow
            label="PASSWORD"
            value="********"
            buttonLabel={userType !== 5 ? "EDIT" : null}
            onButtonClick={() => setIsPasswordModalOpen(true)}
          />

          <DetailRow
            label={`CREDIT BALANCE ${rupee_dollar === 1 ? "$" : ""}`}
            value={formatNum(deposit_total)}
            buttonLabel={userType !== 5 ? "EDIT" : null}
            onButtonClick={() => setIsBalanceModalOpen(true)}
          />

          <DetailRow
            label={`CURRENT BALANCE ${rupee_dollar === 1 ? "$" : ""}`}
            value={formatNum(deposit_current)}
            buttonLabel={userType !== 5 ? "EDIT" : null}
            onButtonClick={() => setIsBalanceModalOpen(true)}
          />

          {nse_cash === 1 && userType === 1 && (
            <DetailRow
              label="CASH DEPOSIT"
              value={formatNum(deposit_interest)}
              buttonLabel={userType !== 5 ? "EDIT" : null}
              onButtonClick={() => setIsCashBalanceModalOpen(true)}
            />
          )}

          {nse_cash === 1 && (
            <DetailRow
              label="DEPOSIT INTEREST"
              value={`${formatNum(
                deposit_total - deposit_interest,
              )} (${deposit_interest_rate}%)`}
            />
          )}

          {auto_sq === 1 && (
            <DetailRow
              label="AUTO SQ %"
              value={formatNum(auto_sq_percentage)}
              buttonLabel={userType !== 5 ? "EDIT" : null}
              onButtonClick={() => setIsAutoSqPModalOpen(true)}
            />
          )}

          <DetailRow
            label={`USE MARGIN ${rupee_dollar === 1 ? "$" : ""}`}
            value={formatNum(parseFloat(limit_blocked) || 0)}
          />
        </div>

        {/* USER STATUS BUTTONS */}
        <div className="flex flex-wrap gap-2 mt-5">
          <ResetPassword user_id={id} />
          {[
            ["COMMISSION SHOW", "commission_show", commission_show],
            ["ACTIVE", "status", status],
            ["ALERT", "alert", alert],
            ["WATCH", "watch", watch],
            ["FRESH LIMIT", "fresh_limit", fresh_limit],
            ["OPTIONS SELL", "options_sell", options_sell],
            ["ONLY SQ", "only_sq", only_sq],
            ["AUTO SQ", "auto_sq", auto_sq],
            ["TRADE", "trade", trade],
            ["LEDGER VIEW", "ledger_view", ledger_view],
            ["BLOCK SYMBOL", "block_symbol_allow", block_symbol_allow],
          ].map(([label, key, val]) => (
            <StatusButton
              key={key}
              label={label}
              user_id={user_id}
              user_label={key}
              user_status={val}
              userType={userType}
            />
          ))}
        </div>
      </div>

      {/* ------------------------------------
          COLUMN 2 — USER EXCHANGES + SETTINGS
      --------------------------------------*/}
      <div
        style={{
          overflowY: "scroll",
          height: containerHeight - 20,
          paddingBottom: 200,
        }}
        className="bg-[var(--background)] rounded-xl p-5 pb-0  shadow-inner border-2 border-[var(--hover)]"
      >
        <h3 className="font-bold text-[var(--text)] text-lg mb-3">
          🌐 User Exchanges
        </h3>

        <SectionChips
          reload={loadUser}
          user={user}
          userType={userType}
          chips={[
            { key: "nse", label: "NSE Future", status: master.nse },
            { key: "nse_cash", label: "NSE Equity", status: master.nse_cash },
            { key: "options", label: "NSE Options", status: master.options },
            { key: "mcx", label: "MCX Future", status: master.mcx },
            {
              key: "mcx_options",
              label: "MCX Options",
              status: master.mcx_options,
            },
            {
              key: "comex",
              label: "COMEX",
              status: master.comex,
            },
            {
              key: "crypto",
              label: "Crypto",
              status: master.crypto,
            },
            {
              key: "forex",
              label: "Forex",
              status: master.forex,
            },
            { key: "sgx", label: "SGX", status: master.sgx },
            { key: "us_stock", label: "US Stock", status: master.us_stock },
            { key: "index", label: "Others", status: master.index },
            { key: "dgcx", label: "Dgcx", status: master.dgcx },
            { key: "ncdex", label: "Ncdex", status: master.ncdex },
          ]}
        />

        <h3 className="font-bold text-[var(--text)] text-lg mb-3 mt-3">
          🌐 User Broker
        </h3>

        <div className="space-y-2 text-sm">
          {broker_id === 0 ? (
            <DetailRow
              label="BROKER NAME"
              value={broker_id === 0 ? "NO-BROKER" : broker?.username}
              buttonLabel={userType !== 5 ? "EDIT" : null}
              onButtonClick={() => setIsBrokerModalOpen(true)}
            />
          ) : (
            <DetailRow label="BROKER NAME" value={broker?.username} />
          )}

          {broker_id === 0 ? null : (
            <DetailRow
              label="BROKER SHARING %"
              value={user?.bb_sharing}
              buttonLabel={userType !== 5 ? "EDIT" : null}
              onButtonClick={() => setIsBrokeSharingModalOpen(true)}
            />
          )}
        </div>

        <h3 className="font-bold text-lg text-[var(--text)] mt-5 mb-3">
          ⚙️ User Other
        </h3>

        {[
          ["SQ-OFF TIME", "sq_time"],
          ["LIMIT %", "limit_p"],
          ["PROFIT LIMIT", "profit_limit"],
          ["STANDING LIMIT", "standing_limit"],
          ["MIN RATE", "min_rate"],
          ["TOTAL MCX LOT", "max_lot"],
        ].map(([label, key]) => (
          <div
            key={key}
            className={`flex justify-between items-center ${key === "max_lot" ? "border-b-0" : "border-b-2"
              }  border-[var(--hover)] pb-1 text-sm `}
          >
            <span className="font-bold  bg-[var(--background)] text-[var(--text)] ">
              {label}: {user[key]}
            </span>

            <ActionButton
              label="SUBMIT"
              item={key}
              user={user}
              reload={loadUser}
              userType={userType}
            />
          </div>
        ))}
      </div>

      {/* ------------------------------------
          COLUMN 3 — EXCHANGE SETTINGS
      --------------------------------------*/}
      <div
        className="bg-[var(--background)] rounded-xl p-5 pb-0  shadow-inner border-2 border-[var(--hover)]"
        style={{
          overflowY: "scroll",
          height: containerHeight - 20,
          paddingBottom: 200,
        }}
      >
        <h3 className="font-bold text-lg text-[var(--text)] mb-3">
          ⚡ Exchange Settings
        </h3>

        <div className="space-y-6">
          {[
            ["nse", "📈 NSE Future"],
            ["nse_cash", "📈 NSE Equity"],
            ["options", "📝 NSE Options"],
            ["mcx", "💹 MCX Future"],
            ["mcx_options", "💹 MCX Options"],
            ["comex", "🌍 COMEX"],
            ["crypto", "₿ Crypto"],
            ["forex", "💱 Forex"],
            ["us_stock", "📈 US Stock"],
            ["sgx", "📈 Sgx"],
            ["index", "🇺🇸 Others"],
            ["dgcx", "🇺🇸 Dgcx"],
            ["ncdex", "🇺🇸 Ncdex"],
          ].map(([key, label]) => {
            if (user[key] !== 1) return null;

            const limitKey = `${key}_limit`;
            const midLimitKey = `${key}_mid_limit`;
            const marginKey = `margin_${key}`;
            const marginHoldKey = `margin_${key}_carry`;
            const commissionKey =
              key === "mcx"
                ? user.lot_volume === 0
                  ? `b_${key}_lot`
                  : `b_${key}_volume`
                : `b_${key}`;
            const brokerKey = `bb_${key}`;
            const subbrokerKey = `sb_${key}`;
            const type =
              key === "mcx"
                ? user.lot_volume === 0
                  ? "L"
                  : "V"
                : key === "nse" ||
                  key === "sgx" ||
                  key === "us_stock" ||
                  key === "index"
                  ? `V`
                  : "L";

            return (
              <div
                key={key}
                className="space-y-3 border-b border-[var(--hover)] pb-4"
              >
                <div className="flex justify-between items-center">
                  <h4 className="font-bold text-[var(--text)]">{label}</h4>

                  <button
                    className="px-3 py-1 bg-[var(--primary)] text-white rounded-full text-xs font-semibold"
                    onClick={() => {
                      if (userType === 5 || userType === 6) {
                        toast.error("Permission Not Allowed.");
                      } else {
                        setSelectedType({ key, title: label });
                        setIsCommissionModalOpen(true);
                      }
                    }}
                  >
                    EDIT
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-10">
                  <StatusButton
                    label="LIMIT"
                    user_id={id}
                    user_label={limitKey}
                    user_status={user[limitKey]}
                    userType={userType}
                  />
                  {key === "mcx" ? (
                    <div>
                      <StatusButton
                        label={user.lot_volume === 0 ? "L-WISE" : "V-WISE"}
                        user_id={id}
                        user_label={"LOT_VOLUME"}
                        user_status={user.lot_volume}
                        fetchUsers={loadUser}
                        reload={1}
                        userType={userType}
                      />
                    </div>
                  ) : (
                    <div />
                  )}

                  <StatusButton
                    label="MID LIMIT"
                    user_id={id}
                    user_label={midLimitKey}
                    user_status={user[midLimitKey]}
                    userType={userType}
                  />
                </div>

                <EditRow
                  exchange={key}
                  user={user}
                  label1={user[marginKey]}
                  label2={user[marginHoldKey]}
                  label3={user[commissionKey]}
                  label4={user[brokerKey]}
                  label5={user[subbrokerKey]}
                  broker_id={broker_id}
                  sub_broker_id={sub_broker_id}
                  type={type}
                  rupee_dollar={rupee_dollar}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* ------------------------------------
          MODALS
      --------------------------------------*/}
      <EditNameModal
        open={isNameModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
      <EditPasswordModal
        open={isPasswordModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
      <EditBalance
        open={isBalanceModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
      <EditCashDeposit
        open={isCashBalanceModalOpen}
        onClose={closeAllModals}
        master={master}
        user={user}
        reload={loadUser}
      />
      <AutoSqP
        open={isAutoSqPModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
      <EditCommission
        open={isCommissionModalOpen}
        onClose={closeAllModals}
        user={user}
        master={master}
        type={selectedType}
        fetchUsers={loadUser}
      />
      <EditBroker
        open={isBrokerModalOpen}
        onClose={closeAllModals}
        user={user}
        brokerlist={brokerlist}
        fetchUsers={loadUser}
      />
      <EditBrokerSharing
        open={isBrokeSharingModalOpen}
        onClose={closeAllModals}
        user={user}
        master={master}
        brokerlist={brokerlist}
        fetchUsers={loadUser}
      />
    </div>
  );
}

/* -------------------------------------------------- */
/* COMPONENTS */
/* -------------------------------------------------- */

function DetailRow({ label, value, buttonLabel, onButtonClick }) {
  return (
    <div className="flex justify-between border-b-2 border-[var(--hover)] pb-2 items-center">
      <span className="font-bold text-[var(--text)] text-[14px] ">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-bold text-[var(--text)] text-[14px] ">
          {value}
        </span>

        {buttonLabel && (
          <button
            onClick={onButtonClick}
            className="px-2 py-0.5 bg-[var(--primary)] text-white rounded-full text-xs font-semibold"
          >
            {buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function SectionChips({ chips, user, reload, userType }) {
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) =>
        chip.status === 1 ? (
          <StatusButton
            key={chip.key}
            label={chip.label}
            user_id={user.id}
            user_label={chip.key}
            user_status={user[chip.key]}
            fetchUsers={reload}
            reload={1}
            userType={userType}
          />
        ) : null,
      )}
    </div>
  );
}

function EditRow({
  exchange,
  user,
  label1,
  label2,
  label3,
  label4,
  label5,
  broker_id,
  sub_broker_id,
  type,
  rupee_dollar,
}) {
  const isDollar =
    exchange === "comex" ||
    exchange === "crypto" ||
    exchange === "forex" ||
    exchange === "us_stock" ||
    exchange === "dgcx" ||
    exchange === "index";

  // Determine grid classes based on broker_id and sub_broker_id
  const getGridClasses = () => {
    const hasBroker = broker_id !== 0;
    const hasSubBroker = sub_broker_id !== 0;

    if (hasBroker && hasSubBroker) {
      return user.intraday_sq === 1 ? "grid-cols-5 gap-5" : "grid-cols-4 gap-4";
    } else if (hasBroker) {
      return user.intraday_sq === 1 ? "grid-cols-4 gap-4" : "grid-cols-3 gap-3";
    } else if (hasSubBroker) {
      return user.intraday_sq === 1 ? "grid-cols-4 gap-4" : "grid-cols-3 gap-3"; // If only sub_broker exists, still use 4 columns
    } else {
      return user.intraday_sq === 1 ? "grid-cols-3 gap-3" : "grid-cols-2 gap-2";
    }
  };

  const gridClasses = getGridClasses();

  return (
    <div className={`grid ${gridClasses} text-xs font-semibold`}>
      <div>
        <span className="text-[var(--subtext)] text-[10px]">MARGIN</span>
        <div className="font-bold text-[var(--text)]">{label1}</div>
      </div>

      {user.intraday_sq === 1 ? (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">MARGIN-H</span>
          <div className="font-bold text-[var(--text)]">{label2}</div>
        </div>
      ) : null}

      <div>
        <span className="text-[var(--subtext)] text-[10px]">COMMISSION</span>
        <div className="font-bold text-[var(--text)]">
          {rupee_dollar === 0 ? "₹" : isDollar ? "$" : "₹"}
          {label3} {type}
        </div>
      </div>

      {broker_id !== 0 && (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">BROKER</span>
          <div className="font-bold text-[var(--text)]">
            {exchange === "mcx"
              ? user.lot_volume === 0
                ? label4
                : parseFloat(label4)
              : label4}{" "}
            {type}
          </div>
        </div>
      )}

      {sub_broker_id !== 0 && (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">SUB BROKER</span>
          <div className="font-bold text-[var(--text)]">
            {label5} {type}
          </div>
        </div>
      )}
    </div>
  );
}
